import React, { useEffect, useState } from "react";
import ProductCard from "./components/ProductCard";
import Pagination from "./components/Pagination";
import SearchCard from "./components/SearchCard";
import { useSearchParams } from "react-router-dom";
import { ListFilter } from "lucide-react";

import useApiClient from "../../api/client";
import config from "../../config/env";
import SelectField from "../../components/inputs/SelectField";

function joinImgUrl(base, path) {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return b && p ? `${b}/${p}` : "";
}

function resolveImageUrl(image) {
    if (!image) return "";
    const path =
        image?.formats?.medium?.url ||
        image?.formats?.small?.url ||
        image?.formats?.thumbnail?.url ||
        image?.url ||
        image?.attributes?.formats?.medium?.url ||
        image?.attributes?.formats?.small?.url ||
        image?.attributes?.formats?.thumbnail?.url ||
        image?.attributes?.url ||
        image?.data?.attributes?.formats?.medium?.url ||
        image?.data?.attributes?.formats?.small?.url ||
        image?.data?.attributes?.formats?.thumbnail?.url ||
        image?.data?.attributes?.url ||
        "";
    return joinImgUrl(config.apiImg, path);
}

function pickFirstImageUrl(product) {
    const imgs = product?.images;
    if (Array.isArray(imgs) && imgs.length > 0) {
        return resolveImageUrl(imgs[0]);
    }
    return "";
}

function mapProductToCardItem(p) {
    const productName = String(p?.product_name || p?.model?.category?.name || "").trim();
    const modelName = String(p?.model?.name || "").trim();
    const title = productName && modelName
        ? (productName.toLowerCase() === modelName.toLowerCase() ? modelName : `${productName} ${modelName}`)
        : productName || modelName || "-";
    const colorName = p?.color?.name || "-";
    const capText = p?.capacity?.size || p?.capacity?.name || "-";

    const price = Number(p?.sell_price ?? 0) || Number(p?.buy_price ?? 0) || 0;

    return {
        id: String(p?.id ?? ""),
        title,
        batteryHealth: p?.battery_health,
        usage: String(p?.usage || ""),
        color: colorName,
        capacity: capText,
        detail: String(p?.website_description || p?.description || ""),
        warranty: String(p?.warranty_info || p?.warranty_status || "-"),
        warrantyEndDate: p?.warranty_end_date || p?.warranty_date || "",
        price,
        isOnsale: Boolean(p?.is_onsale),
        onsaleDiscount: Number(p?.onsale_discount ?? 0) || 0,
        image: pickFirstImageUrl(p),
        raw: p,
    };
}

export default function AllProductsPage() {
    const api = useApiClient();
    const [searchParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    const [page, setPage] = useState(1);
    const pageSize = 12; // 4x3
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [productCondition, setProductCondition] = useState("all");
    const [onSaleOnly, setOnSaleOnly] = useState(false);
    const [installmentOnly, setInstallmentOnly] = useState(false);
    const [priceSort, setPriceSort] = useState("all");
    const [productCategory, setProductCategory] = useState("");
    const [modelId, setModelId] = useState("");
    const categoryNameParam = String(searchParams.get("category") || "").trim();
    const productTypeParam = String(searchParams.get("product_type") || "").trim();
    const categoryDisplayName = categoryNameParam === "apple_watch" ? "Apple Watch" : categoryNameParam;
    const pageTitle = categoryDisplayName || "สินค้าทั้งหมด";

    const categoryId = (() => {
        const raw = String(productCategory || "").trim();
        if (!raw) return "";
        if (raw.startsWith("category:")) return raw.split(":")[1] || "";
        return "";
    })();

    const conditionOptions = [
        { value: "all", label: "ตัวกรอง: ทั้งหมด" },
        { value: "new", label: "ตัวกรอง: สินค้ามือ 1" },
        { value: "used", label: "ตัวกรอง: สินค้ามือ 2" },
        { value: "warranty", label: "ตัวกรอง: สินค้าประกันเหลือ" },
    ];

    const sortOptions = [
        { value: "all", label: "เรียงลำดับ: ทั้งหมด" },
        { value: "price_desc", label: "เรียงลำดับ: ราคาสูง-ต่ำ" },
        { value: "price_asc", label: "เรียงลำดับ: ราคาต่ำ-สูง" },
    ];

    useEffect(() => {
        let ignore = false;

        (async () => {
            try {
                setLoading(true);
                setLoadError("");

                const params = {
                    "filters[product_status][$eq]": "available",
                    "pagination[page]": page,
                    "pagination[pageSize]": pageSize,
                    sort:
                        priceSort === "price_asc"
                            ? "sell_price:asc"
                            : priceSort === "price_desc"
                                ? "sell_price:desc"
                                : "createdAt:desc",
                };

                if (modelId) {
                    params["pagination[page]"] = 1;
                    params["pagination[pageSize]"] = 1000;
                }

                let andIdx = 0;

                if (categoryNameParam) {
                    params[`filters[$and][${andIdx}][$or][0][category][name][$eqi]`] = categoryNameParam;
                    params[`filters[$and][${andIdx}][$or][1][model][category][name][$eqi]`] = categoryNameParam;
                    params[`filters[$and][${andIdx}][$or][2][product_name][$eqi]`] = categoryNameParam;
                    params[`filters[$and][${andIdx}][$or][3][product_type][$eqi]`] = categoryNameParam;
                    andIdx += 1;
                }

                if (productTypeParam) {
                    params[`filters[$and][${andIdx}][product_type][$eqi]`] = productTypeParam;
                    andIdx += 1;
                }

                if (categoryId) {
                    params[`filters[$and][${andIdx}][$or][0][model][category][id][$eq]`] = categoryId;
                    params[`filters[$and][${andIdx}][$or][1][category][id][$eq]`] = categoryId;
                    andIdx += 1;
                }

                if (searchQuery.trim()) {
                    const q = searchQuery.trim();
                    params[`filters[$and][${andIdx}][$or][0][product_name][$containsi]`] = q;
                    params[`filters[$and][${andIdx}][$or][1][model][name][$containsi]`] = q;
                    params[`filters[$and][${andIdx}][$or][2][category][name][$containsi]`] = q;
                    params[`filters[$and][${andIdx}][$or][3][website_description][$containsi]`] = q;
                    params[`filters[$and][${andIdx}][$or][4][description][$containsi]`] = q;
                    andIdx += 1;
                }

                if (productCondition === "new") {
                    params[`filters[$and][${andIdx}][usage][$eq]`] = "new";
                    andIdx += 1;
                }

                if (productCondition === "used") {
                    params[`filters[$and][${andIdx}][usage][$eq]`] = "used";
                    andIdx += 1;
                }

                if (productCondition === "warranty") {
                    params[`filters[$and][${andIdx}][$or][0][warranty_info][$containsi]`] = "ประกันศูนย์";
                    params[`filters[$and][${andIdx}][$or][1][warranty_info][$containsi]`] = "1 ปี";
                    andIdx += 1;
                }

                if (onSaleOnly) {
                    params[`filters[$and][${andIdx}][is_onsale][$eq]`] = true;
                    andIdx += 1;
                }

                if (installmentOnly) {
                    params[`filters[$and][${andIdx}][installment_plan][$notNull]`] = true;
                    params[`filters[$and][${andIdx}][installment_plan][$ne]`] = "";
                }

                const res = await api.get("products", {
                    ...params,
                });

                if (ignore) return;

                const list = res?.data?.data || [];
                const mapped = (Array.isArray(list) ? list : []).map(mapProductToCardItem);

                if (modelId) {
                    const filteredByModel = mapped.filter((item) => {
                        const raw = item?.raw || {};
                        const rawModelId = String(raw?.model?.id ?? raw?.model?.documentId ?? "").trim();
                        return rawModelId === String(modelId);
                    });

                    const nextPageCount = Math.max(1, Math.ceil(filteredByModel.length / pageSize));
                    const safePage = Math.min(page, nextPageCount);
                    const start = (safePage - 1) * pageSize;

                    setRows(filteredByModel.slice(start, start + pageSize));
                    setTotalPages(nextPageCount);
                } else {
                    setRows(mapped);
                    const nextPageCount = Number(res?.data?.meta?.pagination?.pageCount || 1);
                    setTotalPages(Math.max(1, nextPageCount));
                }
            } catch (e) {
                console.error(e);
                if (!ignore) {
                    setRows([]);
                    setTotalPages(1);
                    setLoadError("โหลดข้อมูลไม่สำเร็จ");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [page, pageSize, searchQuery, categoryNameParam, productTypeParam, productCondition, onSaleOnly, installmentOnly, priceSort, categoryId, modelId]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, categoryNameParam, productTypeParam, productCondition, onSaleOnly, installmentOnly, priceSort, productCategory, modelId]);

    return (
        <div>
            {/* HERO TITLE */}
            <div className="bg-[#1B3147B2] backdrop-blur-xl h-[55px]" />
            <section className="h-[70px] bg-gradient-to-b from-[#73B7F5]/60 via-[#89C2F6]/20 to-white" />

            {/* ส่วนหัวข้อความอยู่บนพื้นขาว */}
            <section className="bg-white">
                <div className="mx-auto max-w-6xl px-6 py-2 text-center">
                    <div className="text-4xl md:text-5xl font-medium text-[#2F6FB4]">
                        {pageTitle}
                    </div>
                    <div className="text-[#1D1616] text-sm mt-1">Our Product</div>
                </div>
            </section>

            <div className="mx-auto max-w-6xl px-6 mt-10 relative z-10">
                <SearchCard
                    value={{ q: searchQuery, cat: productCategory }}
                    onQueryChange={setSearchQuery}
                    onCategoryChange={setProductCategory}
                    modelValue={modelId}
                    onModelChange={setModelId}
                    productPlaceholder="เลือกสินค้า"
                    modelPlaceholder="เลือกรุ่น"
                    onSearch={() => setPage(1)}
                    title={categoryNameParam ? `ค้นหา ${categoryNameParam}` : "ค้นหาสินค้า"}
                    subtitle={
                        categoryNameParam
                            ? `สินค้า ${categoryNameParam} สินค้าทั้งหมดในร้านเบสท์โฟนเชียงใหม่`
                            : "สินค้าทั้งหมดในร้านเบสท์โฟนเชียงใหม่"
                    }
                    queryPlaceholder={categoryNameParam ? `ค้นหา ${categoryNameParam}` : "ค้นหาชื่อสินค้า"}
                />
            </div>

            <section className="mx-auto max-w-6xl px-6 py-10">

                <div className="text-center mt-10 mb-6">
                    <div className="sm:text-xs md:text-md text-[#047DED] font-semibold">Product List</div>
                    <div className="sm:text-xl md:text-3xl font-extrabold text-slate-900 mt-2">รายการสินค้า</div>
                </div>

                <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                        <div className="w-full md:w-[240px]">
                            <SelectField
                                icon={ListFilter}
                                options={conditionOptions}
                                value={productCondition}
                                onChange={(v) => setProductCondition(String(v || "all"))}
                                placeholder="ตัวกรอง: ทั้งหมด"
                                buttonClassName="h-11 rounded-xl bg-white border border-slate-200"
                                menuClassName="z-[8000]"
                            />
                        </div>

                        <label className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                            <input
                                type="checkbox"
                                checked={onSaleOnly}
                                onChange={(e) => setOnSaleOnly(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-[#047DED] focus:ring-[#047DED]"
                            />
                            <span>สินค้ากำลังลดราคา⚡</span>
                        </label>

                        <label className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                            <input
                                type="checkbox"
                                checked={installmentOnly}
                                onChange={(e) => setInstallmentOnly(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-[#047DED] focus:ring-[#047DED]"
                            />
                            <span>มีผ่อนชำระ</span>
                        </label>
                    </div>

                    <div className="w-full md:w-[260px] md:ml-auto">
                        <SelectField
                            icon={ListFilter}
                            options={sortOptions}
                            value={priceSort}
                            onChange={(v) => setPriceSort(String(v || "all"))}
                            placeholder="เรียงลำดับ: ทั้งหมด"
                            buttonClassName="h-11 rounded-xl bg-white border border-slate-200"
                            menuClassName="z-[8000]"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {rows.map((p) => (
                        <ProductCard key={p.id} item={p} />
                    ))}
                </div>

                {!loading && !loadError && rows.length === 0 && (
                    <div className="mt-6 text-center text-sm font-semibold text-slate-500">
                        ไม่พบสินค้าที่ตรงกับตัวกรอง
                    </div>
                )}

                {loadError && (
                    <div className="mt-6 text-center text-sm font-semibold text-rose-600">
                        {loadError}
                    </div>
                )}

                {loading && (
                    <div className="mt-6 text-center text-sm font-semibold text-slate-500">
                        กำลังโหลด...
                    </div>
                )}

                <div className="mt-10 flex justify-center">
                    <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
            </section>
        </div>
    );
}
